// ── magic byte signatures ─────────────────────────────────────────────────────
// Each entry matches the first N bytes of a file to a MIME type and extension.
// Order matters: more-specific signatures must come before less-specific ones.
const SIGNATURES: Array<{ bytes: number[]; ext: string; mime: string }> = [
  { bytes: [0x25, 0x50, 0x44, 0x46], ext: ".pdf",  mime: "application/pdf" },
  { bytes: [0x50, 0x4b, 0x03, 0x04], ext: ".zip",  mime: "application/zip" }, // ZIP / DOCX / XLSX / PPTX
  { bytes: [0xd0, 0xcf, 0x11, 0xe0], ext: ".doc",  mime: "application/msword" }, // OLE2: DOC / XLS
  { bytes: [0x89, 0x50, 0x4e, 0x47], ext: ".png",  mime: "image/png" },
  { bytes: [0xff, 0xd8, 0xff],       ext: ".jpg",  mime: "image/jpeg" },
];

// ── MIME → extension map ──────────────────────────────────────────────────────
const MIME_EXT: Record<string, string> = {
  "application/pdf":            ".pdf",
  "application/msword":         ".doc",
  "application/vnd.ms-excel":   ".xls",
  "text/csv":                   ".csv",
  "text/plain":                 ".txt",
  "image/png":                  ".png",
  "image/jpeg":                 ".jpg",
  // OOXML formats
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":   ".docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":         ".xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
};

// ZIP-based OOXML: content-type disambiguates DOCX vs XLSX vs PPTX when magic
// bytes only tell us "it's a ZIP".
const OOXML_BY_MIME: Record<string, string> = {
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":   ".docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":         ".xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
};

// OLE2-based: content-type disambiguates DOC vs XLS.
const OLE2_BY_MIME: Record<string, string> = {
  "application/msword":       ".doc",
  "application/vnd.ms-excel": ".xls",
};

// ── helpers ───────────────────────────────────────────────────────────────────

function parseMime(raw: string): string {
  return (raw ?? "").split(";")[0].trim().toLowerCase();
}

function filenameFromDisposition(disposition: string): string | null {
  if (!disposition) return null;
  // RFC 5987 encoded: filename*=UTF-8''encoded%20name.pdf
  const rfc5987 = disposition.match(/filename\*=UTF-8''([^;\r\n]+)/i);
  if (rfc5987) return decodeURIComponent(rfc5987[1].trim());
  // Plain: filename="name.pdf" or filename=name.pdf
  const plain = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\r\n]*)/i);
  if (plain) return plain[1].replace(/^["']|["']$/g, "").trim();
  return null;
}

async function extFromMagicBytes(blob: Blob, mime: string): Promise<string> {
  try {
    const buf = await blob.slice(0, 8).arrayBuffer();
    const bytes = Array.from(new Uint8Array(buf));
    for (const sig of SIGNATURES) {
      if (sig.bytes.every((b, i) => bytes[i] === b)) {
        if (sig.ext === ".zip") return OOXML_BY_MIME[mime] ?? ".docx";
        if (sig.ext === ".doc") return OLE2_BY_MIME[mime] ?? ".doc";
        return sig.ext;
      }
    }
  } catch {
    // ignore ArrayBuffer read errors
  }
  return "";
}

// ── public API ────────────────────────────────────────────────────────────────

/**
 * Resolves the download filename from Axios response headers + blob data.
 *
 * Priority:
 *   1. `Content-Disposition: filename=…` (backend-supplied, most reliable)
 *   2. Known `Content-Type` → standard extension
 *   3. Magic-byte sniff of the first 8 bytes (handles `application/octet-stream`)
 *   4. Bare fallbackId with no extension
 */
export async function resolveFilename(
  blob: Blob,
  headers: Record<string, string>,
  fallbackId: string,
): Promise<string> {
  const disposition = headers["content-disposition"] ?? "";
  const mime = parseMime(headers["content-type"] ?? "");

  // 1. Backend-supplied filename
  const fromHeader = filenameFromDisposition(disposition);
  if (fromHeader) return fromHeader;

  // 2. Known MIME type
  const knownExt = MIME_EXT[mime];
  if (knownExt) return `${fallbackId}${knownExt}`;

  // 3. Magic bytes (handles application/octet-stream)
  const detectedExt = await extFromMagicBytes(blob, mime);
  return `${fallbackId}${detectedExt}`;
}

/**
 * Triggers a browser file download for the given Blob without leaving the page.
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
